import { z } from 'zod';
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'use HH:MM');
/** One day's window; null means the driver does not take pickups that day. */
export const DayHoursSchema = z.object({ from: HHMM, to: HHMM }).nullable();
/** Seven entries, Sunday first. Null overall means any time. */
export const WeeklyHoursSchema = z.array(DayHoursSchema).length(7).nullable();
const minutes = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
};
/** Local wall-clock parts of an instant in a time zone. */
export function localParts(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(at);
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    const hour = Number(get('hour')) % 24;
    return {
        weekday,
        minutes: hour * 60 + Number(get('minute')),
        ymd: `${get('year')}-${get('month')}-${get('day')}`,
    };
}
/** The YYYY-MM-DD date `days` after `ymd`; calendar arithmetic, so a daylight-saving change never skips or repeats a day. */
export function addDaysYmd(ymd, days) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/**
 * The instant a wall-clock time happens in `timeZone` (`minutes` after midnight on `ymd`).
 * Corrects for the zone's offset twice, so days with a clock change come out right.
 */
export function zonedTime(ymd, minutes, timeZone) {
    const [y, m, d] = ymd.split('-').map(Number);
    const want = Date.UTC(y, m - 1, d) + minutes * 60_000;
    let t = want;
    for (let i = 0; i < 3; i++) {
        const p = localParts(new Date(t), timeZone);
        const [py, pm, pd] = p.ymd.split('-').map(Number);
        const wall = Date.UTC(py, pm - 1, pd) + p.minutes * 60_000;
        if (wall === want)
            break;
        t += want - wall;
    }
    return new Date(t);
}
/** Whether a pickup at `at` falls inside the driver's weekly hours. */
export function withinHours(hours, at, timeZone) {
    if (!hours)
        return true;
    const { weekday, minutes: m } = localParts(at, timeZone);
    const day = hours[weekday];
    if (!day)
        return false;
    const from = minutes(day.from);
    const to = minutes(day.to);
    // A window like 20:00–04:00 wraps past midnight.
    return from <= to ? m >= from && m <= to : m >= from || m <= to;
}
export const DEFAULT_HOURS = null;
//# sourceMappingURL=hours.js.map